/**
 * Keeps the embroidery fee cart line's quantity matched to the total
 * quantity of embroidered lines for the same product, sitewide - and, if a
 * product's fee has been removed from the cart entirely, strips that
 * product's embroidery line-item properties back off so the cart (and the
 * eventual order) doesn't keep advertising embroidery that was never paid
 * for and won't be fulfilled.
 *
 * A Shopify line-item property can't change what a product costs, so the
 * fee rides along as its own cart line (added in embroidery-builder.js,
 * right after the embroidered product itself is added). Adding is covered
 * there via the theme's own "theme:product:add" event, but Broadcast
 * doesn't dispatch a public event when a quantity-change or remove
 * finishes for the cart drawer's own stepper/remove controls or the cart
 * page's quantity field - so shrinking or removing an embroidered line, or
 * removing the fee line itself, needs a different mechanism to stay in
 * sync. This reconciles by reading the authoritative cart (GET /cart.js)
 * whenever something plausibly changed it, correcting any fee line whose
 * quantity has drifted from the embroidered quantity it belongs to, and
 * stripping embroidery properties off any product line whose fee has stayed
 * missing for longer than a short grace window (see STRIP_GRACE_MS) rather
 * than the instant it's first seen missing - a product briefly looking
 * "orphaned" while its own fee-add request is still in flight is normal,
 * not a real removal, and shouldn't get punished for losing that race.
 *
 * Runs once globally, independent of whether any <embroidery-builder>
 * instance exists on the current page - the cart drawer can be opened from
 * any page, not only the product page an embroidered item was added from.
 */
(function () {
  'use strict';

  if (window.embroideryFeeSyncInitialized) return;
  window.embroideryFeeSyncInitialized = true;

  var RECONCILE_DEBOUNCE_MS = 500;
  var reconcileTimer = null;

  function scheduleReconcile() {
    clearTimeout(reconcileTimer);
    reconcileTimer = setTimeout(reconcile, RECONCILE_DEBOUNCE_MS);
  }

  /**
   * A product can look "orphaned" (embroidered, no matching fee yet) for a
   * moment that has nothing to do with the fee actually being missing -
   * embroidery-builder.js's own fee-add request is still in flight, or some
   * other script on the page (a cart drawer app, etc.) refreshed the cart
   * DOM faster than that request could complete. Stripping on the very
   * first sighting punishes that ordinary race, not a real removal. Each
   * product gets a grace window from its first sighting as orphaned before
   * a strip actually happens; if the fee shows up before that elapses, the
   * timer just clears and nothing is touched.
   */
  var STRIP_GRACE_MS = 4000;
  var orphanedSince = {};

  function reconcile() {
    var feeVariantId = window.theme && window.theme.embroideryFeeVariantId;
    if (!feeVariantId || !window.theme.routes) return;

    fetch(window.theme.routes.cart_url + '.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        return applyReconciliation(cart, String(feeVariantId));
      })
      .catch(function (error) {
        console.error('Embroidery fee reconciliation could not read the cart:', error);
      });
  }

  /**
   * Fee lines are correlated back to the product they belong to via the
   * "Added For" property (the product title) stamped on them when added -
   * see addEmbroideryFee() in embroidery-builder.js. Multiple embroidered
   * lines of the same product (different sizes/colors/designs) already
   * merge into one fee line on add since Shopify groups by variant +
   * properties, so summing by product title here matches that.
   */
  function applyReconciliation(cart, feeVariantId) {
    var expectedByProduct = {};
    cart.items.forEach(function (item) {
      if (!item.properties || item.properties['Embroidery'] !== 'Yes') return;
      expectedByProduct[item.product_title] = (expectedByProduct[item.product_title] || 0) + item.quantity;
    });

    var feeQuantityByProduct = {};
    var quantityUpdates = {};
    cart.items.forEach(function (item) {
      if (String(item.variant_id) !== feeVariantId) return;
      var addedFor = item.properties && item.properties['Added For'];
      if (!addedFor) return;

      feeQuantityByProduct[addedFor] = (feeQuantityByProduct[addedFor] || 0) + item.quantity;

      var expected = expectedByProduct[addedFor] || 0;
      if (item.quantity !== expected) quantityUpdates[item.key] = expected;
    });

    var now = Date.now();
    var seenThisPass = {};
    var orphanedLines = [];

    cart.items.forEach(function (item) {
      if (!item.properties || item.properties['Embroidery'] !== 'Yes') return;
      var title = item.product_title;

      if (feeQuantityByProduct[title]) {
        delete orphanedSince[title];
        return;
      }

      seenThisPass[title] = true;
      if (!orphanedSince[title]) {
        orphanedSince[title] = now;
      } else if (now - orphanedSince[title] >= STRIP_GRACE_MS) {
        orphanedLines.push(item);
      }
    });

    // Anything not seen as orphaned this pass (its fee showed up, or the
    // line itself is gone) shouldn't keep a stale timer around.
    Object.keys(orphanedSince).forEach(function (title) {
      if (!seenThisPass[title]) delete orphanedSince[title];
    });

    // Still-pending grace windows need a follow-up check even if nothing
    // else touches the cart in the meantime, or a fee-add that genuinely
    // never arrives would just sit unstrip until some unrelated trigger.
    if (Object.keys(seenThisPass).length && !orphanedLines.length) {
      setTimeout(scheduleReconcile, STRIP_GRACE_MS + 250);
    }

    var updateFeeQuantities = Object.keys(quantityUpdates).length
      ? applyQuantityUpdates(quantityUpdates)
      : Promise.resolve();

    return updateFeeQuantities.then(function () {
      if (!orphanedLines.length) return;
      return stripEmbroideryFromLines(orphanedLines);
    });
  }

  function applyQuantityUpdates(updates) {
    return fetch(window.theme.routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates }),
    })
      .then(function (response) {
        return response.json();
      })
      .then(function () {
        document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
      })
      .catch(function (error) {
        console.error('Embroidery fee reconciliation could not update the cart:', error);
      });
  }

  /**
   * Line-item properties can't be edited on an existing cart line - the
   * only way to drop them is to add a fresh line for the same variant and
   * quantity with no properties (merging into any matching plain line
   * Shopify already has) and then zero out the old embroidered line. Adds
   * before removing, and one line at a time, so the product is never
   * momentarily missing from the cart and concurrent cart mutations don't
   * clobber each other.
   */
  function stripEmbroideryFromLines(lines) {
    return lines.reduce(function (chain, item) {
      return chain.then(function () {
        return stripEmbroideryFromLine(item);
      });
    }, Promise.resolve());
  }

  function stripEmbroideryFromLine(item) {
    return fetch(window.theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: item.variant_id, quantity: item.quantity }] }),
    })
      .then(function (response) {
        return response.json();
      })
      .then(function () {
        return fetch(window.theme.routes.cart_update_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: { [item.key]: 0 } }),
        });
      })
      .then(function () {
        document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
      })
      .catch(function (error) {
        console.error('Embroidery fee reconciliation could not strip embroidery from a cart line:', error);
      });
  }

  ['theme:cart:refresh', 'theme:cart:change', 'theme:cart-drawer:open', 'theme:product:add'].forEach(function (name) {
    document.addEventListener(name, scheduleReconcile);
  });

  if (window.theme && window.theme.PUB_SUB_EVENTS && typeof window.subscribe === 'function') {
    window.subscribe(window.theme.PUB_SUB_EVENTS.cartUpdate, scheduleReconcile);
  }

  /**
   * Safety net for the cart drawer/cart page's own quantity stepper and
   * remove controls, which complete their AJAX update (POST
   * /cart/change.js) without dispatching any of the events above. Watching
   * for the resulting DOM re-render is the only reliable general signal
   * for "the cart may have changed" available without depending on exact
   * internals that could shift in a future theme update.
   */
  var cartObserver = new MutationObserver(function (mutations) {
    var relevant = mutations.some(function (mutation) {
      var node = mutation.target;
      return node.nodeType === 1 && Boolean(node.closest('[data-api-content], cart-drawer, .cart, [data-item]'));
    });
    if (relevant) scheduleReconcile();
  });

  cartObserver.observe(document.body, { childList: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleReconcile);
  } else {
    scheduleReconcile();
  }
})();